import { publicUrl } from "@/lib/public-url";

export type ExtendedTokenId =
	| "ICP"
	| "ckBTC"
	| "ckETH"
	| "ckUSDC"
	| "USDM"
	| "MMK"
	| "ETH";

export type TokenAsset = {
	id: ExtendedTokenId;
	symbol: string;
	label: string;
	/** Path without canister query — use getTokenIcon() at render time. */
	iconPath: string;
	/** @deprecated use getTokenIcon(id) — raw path is not safe on ?canisterId= hosts */
	icon: string;
};

const BASE = "/assets/tokens";

function token(
	id: ExtendedTokenId,
	symbol: string,
	label: string,
	file: string,
): TokenAsset {
	const iconPath = `${BASE}/${file}`;
	return { id, symbol, label, iconPath, icon: iconPath };
}

export const TOKEN_ASSETS: Record<
	Exclude<ExtendedTokenId, "ETH"> | "ETH",
	TokenAsset
> = {
	ICP: token("ICP", "ICP", "Internet Computer", "icp.png"),
	ETH: token("ETH", "ETH", "Ethereum", "icp.png"),
	ckBTC: token("ckBTC", "ckBTC", "Chain-key Bitcoin", "ckbtc.png"),
	ckETH: token("ckETH", "ckETH", "Chain-key Ethereum", "cketh.png"),
	ckUSDC: token("ckUSDC", "ckUSDC", "Chain-key USDC", "ckusdc.png"),
	USDM: token("USDM", "USDM", "Motoko Dollar", "usdm.png"),
	MMK: token("MMK", "MMK", "MotokoMaker", "mmk.png"),
};

/** Brand logo fallbacks (also under public/assets). */
export const TOKEN_LOGO_FALLBACK: Partial<Record<ExtendedTokenId, string>> = {
	USDM: "/assets/motoko-dollar-logo.png",
	MMK: "/assets/motokomaker-logo.png",
};

export const VAULT_COLLATERAL_ASSETS: TokenAsset[] = [
	TOKEN_ASSETS.ICP,
	TOKEN_ASSETS.ckBTC,
	TOKEN_ASSETS.ckETH,
];

/**
 * Map loose labels from protocol/UI → ExtendedTokenId.
 * Fixes USDC vs ckUSDC, case variants, etc.
 */
export function normalizeTokenId(
	raw: string | null | undefined,
): ExtendedTokenId | null {
	if (!raw) return null;
	const t = raw.trim();
	if (!t) return null;
	const upper = t.toUpperCase().replace(/\s+/g, "");
	const lower = t.toLowerCase();

	if (upper === "ICP" || lower === "icp" || lower.includes("ops icp") || lower === "opsicp")
		return "ICP";
	// Loose "… ICP" sleeve labels (not ck*)
	if (lower.includes("icp") && !lower.includes("ck")) return "ICP";
	if (upper === "ETH" || lower === "eth") return "ETH";
	if (upper === "USDM" || lower.includes("usdm") || lower.includes("motokodollar"))
		return "USDM";
	if (upper === "MMK" || lower.includes("mmk") || lower.includes("motokomaker"))
		return "MMK";
	if (
		upper === "CKBTC" ||
		upper === "CK_BTC" ||
		lower === "ckbtc" ||
		lower.includes("ckbtc") ||
		(lower.includes("btc") && !lower.includes("eth"))
	)
		return "ckBTC";
	if (
		upper === "CKETH" ||
		upper === "CK_ETH" ||
		lower === "cketh" ||
		lower.includes("cketh")
	)
		return "ckETH";
	if (
		upper === "CKUSDC" ||
		upper === "CK_USDC" ||
		upper === "USDC" ||
		lower === "ckusdc" ||
		lower === "usdc" ||
		lower.includes("usdc")
	)
		return "ckUSDC";

	// Exact id match
	if (t in TOKEN_ASSETS) return t as ExtendedTokenId;
	return null;
}

/** Resolved icon URL safe for assets canister (?canisterId=) hosting. */
export function getTokenIcon(id: string): string | undefined {
	const norm = normalizeTokenId(id) ?? (id as ExtendedTokenId);
	const path = TOKEN_ASSETS[norm as keyof typeof TOKEN_ASSETS]?.iconPath;
	return path ? publicUrl(path) : undefined;
}

export function getTokenLogoFallback(id: string): string | undefined {
	const norm = normalizeTokenId(id);
	if (!norm) return undefined;
	const path = TOKEN_LOGO_FALLBACK[norm];
	return path ? publicUrl(path) : undefined;
}
